import { createRouter, createWebHistory } from "vue-router";

// stores
import { useUser } from "@/stores/useUser";
import { useCompanyId } from "@/stores/useCompanyId";
import { useUiFeatures } from "@/stores/useUiFeatures";

// enums
import { Route } from "@/enums/Route";
import { LegacyClientPanelUrl } from "@/enums/LegacyClientPanelUrl";
import { UserFeature } from "@/enums/UserFeature";
import { LocalStorageKey } from "@/enums/StorageKeys/LocalStorageKey";

export const routerInstance = createRouter({
  history: createWebHistory(import.meta.env.VITE_APP_BASE_URL),
  routes: [
    {
      name: Route.CANDIDATES_SEARCH,
      path: "/:companyId/wyszukiwarka-kandydatow",
      // route level code-splitting
      // this generates a separate chunk (CandidateUserProfile.[hash].js) for this route
      // which is lazy-loaded when the route is visited.
      component: () => import("../pages/CandidatesSearch.vue"),
    },
    {
      name: Route.CANDIDATE_PROFILE,
      path: "/:companyId/profil-zawodowy-kandydata/:candidateId",
      component: () => import("../pages/CandidateProfile.vue"),
    },
    {
      name: Route.CANDIDATES_LISTING,
      path: "/:companyId/oferty/:jobOfferId/kandydaci/:tab",
      component: () => import("../pages/CandidatesListing.vue"),
    },
    {
      name: Route.JOB_OFFERS_LISTING_ACTIVE,
      path: "/:companyId/oferty-pracy/aktywne",
      component: () => import("../pages/JobOffersListing.vue"),
    },
    {
      name: Route.JOB_OFFERS_LISTING_INACTIVE,
      path: "/:companyId/oferty-pracy/nieaktywne",
      component: () => import("../pages/JobOffersListing.vue"),
    },
    {
      name: Route.JOB_OFFERS_LISTING_DRAFT,
      path: "/:companyId/oferty-pracy/wersje-robocze",
      component: () => import("../pages/JobOffersListing.vue"),
    },
    {
      name: Route.JOB_OFFER_ADD_STANDARD_PLUS,
      path: "/:companyId/oferta-pracy/dodaj/standard-plus",
      component: () => import("../pages/JobOfferForm.vue"),
      meta: {
        requiredUserFeatures: [UserFeature.STANDARD_PLUS_JOB_OFFER_PACKAGE],
      },
    },
    {
      name: Route.JOB_OFFER_ADD_PREMIUM,
      path: "/:companyId/oferta-pracy/dodaj/premium",
      component: () => import("../pages/JobOfferForm.vue"),
      meta: {
        requiredUserFeatures: [UserFeature.PREMIUM_JOB_OFFER_PACKAGE],
      },
    },
    {
      name: Route.JOB_OFFER_ADD_PRO,
      path: "/:companyId/oferta-pracy/dodaj/pro",
      component: () => import("../pages/JobOfferForm.vue"),
      meta: {
        requiredUserFeatures: [UserFeature.PRO_JOB_OFFER_PACKAGE],
      },
    },
    {
      name: Route.JOB_OFFER_EDIT,
      path: "/:companyId/oferta-pracy/:jobOfferId/edytuj",
      component: () => import("../pages/JobOfferForm.vue"),
    },
    {
      name: Route.JOB_OFFER_DRAFT_EDIT,
      path: "/:companyId/oferta-pracy/:jobOfferId/wersja-robocza/edytuj",
      component: () => import("../pages/JobOfferForm.vue"),
    },
    {
      name: Route.JOB_OFFER_COPY,
      path: "/:companyId/oferta-pracy/:jobOfferId/kopiuj",
      component: () => import("../pages/JobOfferForm.vue"),
    },
    {
      name: Route.JOB_OFFER_RESUME,
      path: "/:companyId/oferta-pracy/:jobOfferId/wznow",
      component: () => import("../pages/JobOfferForm.vue"),
    },
    {
      name: Route.JOB_OFFER_TEMPLATES_LISTING,
      path: "/:companyId/szablony-ofert-pracy",
      component: () => import("../pages/JobOfferTemplatesListing.vue"),
    },
    {
      name: Route.JOB_OFFER_ADD_TEMPLATE,
      path: "/:companyId/szablon-oferty-pracy/dodaj",
      component: () => import("../pages/JobOfferTemplateForm.vue"),
    },
    {
      name: Route.JOB_OFFER_EDIT_TEMPLATE,
      path: "/:companyId/szablon-oferty-pracy/:jobOfferTemplateId/edytuj",
      component: () => import("../pages/JobOfferTemplateForm.vue"),
    },
    {
      name: Route.USER_NOT_ALLOWED,
      path: "/:companyId/uzytkownik-nie-ma-uprawnien",
      component: () => import("../pages/UserNotAllowed.vue"),
    },
  ],
});

routerInstance.beforeEach(async (to, from) => {
  // check if user is logged in
  const userStore = useUser();
  const companyIdStore = useCompanyId();
  const { companyId: companyUrlId } = to.params as { companyId: string };
  const uiFeaturesStore = useUiFeatures();

  if (to.name !== from.name) {
    localStorage.setItem(
      LocalStorageKey.PREVIOUS_ROUTE,
      JSON.stringify({
        name: from.name,
        params: from.params,
        query: from.query,
        hash: from.hash,
        fullPath: from.fullPath,
        meta: from.meta,
        path: from.path,
      }),
    );
  }

  if (!companyUrlId) {
    window.location.href = LegacyClientPanelUrl.PANEL;
    return false;
  }

  if (!uiFeaturesStore.isFetched) {
    await uiFeaturesStore.fetchUiFeatures({
      companyId: companyUrlId,
    });
  }

  if (companyUrlId !== companyIdStore.companyId) {
    companyIdStore.setCompanyId({ companyId: companyUrlId });
    await userStore.fetchUserData({ companyId: companyIdStore.companyId });
    await uiFeaturesStore.fetchUiFeatures({
      companyId: companyIdStore.companyId,
    });
  }

  if (!userStore.isUserLogged)
    await userStore.fetchUserData({ companyId: companyIdStore.companyId });

  if (!userStore.isUserLogged) {
    console.error("User is not logged in. Redirecting to login page...");
    window.location.href = LegacyClientPanelUrl.PANEL;
    return false;
  }

  // check if user has access to route
  const requiredUserFeatures = to.meta.requiredUserFeatures as UserFeature[];

  if (!requiredUserFeatures) return true;

  const isUserAllowedToSeePage = requiredUserFeatures.every(
    (requiredUserFeature) => userStore.userFeatures[requiredUserFeature],
  );

  if (!isUserAllowedToSeePage) {
    console.error(
      "User is not allowed to see this page. Redirecting to user not allowed page...",
    );
    return {
      name: Route.USER_NOT_ALLOWED,
      params: { companyId: companyIdStore.companyId },
    };
  }

  return true;
});

// Vue's default scrollBehavior method doesn't work when scroll is inside different element than body
routerInstance.afterEach(() => {
  const scrollPlaceholder = document.querySelector(".scroll-placeholder");

  scrollPlaceholder?.scrollIntoView({
    behavior: "smooth",
  });
});
